/**
 * A Worker's status as one small dot: what the Worker is doing right now.
 *
 * Read-only, and deliberately only status. Which provider or model the Worker runs is
 * identity, has its own colors, and never leaks into this dot. The meaning is decided in
 * lib/worker-status-view and tested there; this file only draws what was decided.
 */
import type { ReactElement } from 'react';
import { workerStatusLook } from '../lib/worker-status-view';
import '../worker-status-indicator.css';

export interface WorkerStatusIndicatorProps {
  /** The status the platform says, e.g. `working` or `waiting_for_approval`. */
  readonly status: string;
  /** Size in pixels; the dot scales its ring and glyph to fill it. */
  readonly size?: number;
  /** Override for the accessible name, when the surface has better words. */
  readonly label?: string;
}

/** The check a completed Worker earns, drawn once the state is steady. */
function CheckGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3.5 8.5 6.5 11.5 12.5 4.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The pause a paused Worker holds. */
function PauseGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="4" y="3.5" width="2.6" height="9" rx="0.8" fill="currentColor" />
      <rect x="9.4" y="3.5" width="2.6" height="9" rx="0.8" fill="currentColor" />
    </svg>
  );
}

/** The stop a stopped Worker rests at. */
function StopGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="4" y="4" width="8" height="8" rx="1.4" fill="currentColor" />
    </svg>
  );
}

export function WorkerStatusIndicator({ status, size = 12, label }: WorkerStatusIndicatorProps): ReactElement {
  const look = workerStatusLook(status);
  const glyph = look.shape === 'check' ? <CheckGlyph /> : look.shape === 'pause' ? <PauseGlyph /> : look.shape === 'stop' ? <StopGlyph /> : null;
  return (
    <span
      className={`worker-status worker-status--${look.tone} worker-status--${look.motion}`}
      role="status"
      aria-label={label ?? look.label}
      data-status={look.key}
      style={{ width: size, height: size }}
    >
      {glyph}
    </span>
  );
}
